import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import Callout from '../../Common/Callout';
import StatusBadge from '../../Common/StatusBadge';
import formDialog from '../../Common/formDialog';
import { Section } from '../Personell/EmployeeDetailParts';
import '../Personell/employee-form.css';
import '../../Common/sub-steps.css';
import './payslip.css';
import './semester.css';
import { Calculator, BookOpen, CheckCircle, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
// PageTitle lives on the composite SemesterPage that mounts this section.
import * as payrollApi from '../../../../services/api/payrollApi';
import { toast } from 'sonner';

/**
 * SemesterskuldPage — per-employee semesterskuld preview & booking.
 *
 * Flow:
 *   1. Choose asOf date (default today).
 *   2. Preview liability → shows total 2940 target + per-employee breakdown.
 *   3. Preview adjustment → compares target with current 2940 balance,
 *      returns delta (debit 7290 / credit 2940 or the inverse).
 *   4. Book → POST adjustment. Idempotent: re-posting the same asOf is a no-op
 *      because target matches current balance after the first run.
 *
 * Backend: /payroll/semesterskuld/{liability|adjustment|book}
 */
const SemesterskuldSection = () => {
  const { companyId } = useParams();

  const [asOf, setAsOf] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [description, setDescription] = useState('');
  const [liability, setLiability] = useState(null);
  const [adjustment, setAdjustment] = useState(null);
  const [loadingLiability, setLoadingLiability] = useState(false);
  const [loadingAdjustment, setLoadingAdjustment] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState(null);

  const fmt = (n) => new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

  const handleLiability = async () => {
    setLoadingLiability(true);
    setError(null);
    setAdjustment(null);
    try {
      const res = await payrollApi.getSemesterskuldLiability(companyId, asOf);
      setLiability(res.data?.data || res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte beräkna semesterskuld.');
    } finally {
      setLoadingLiability(false);
    }
  };

  const handleAdjustment = async () => {
    setLoadingAdjustment(true);
    setError(null);
    try {
      const res = await payrollApi.getSemesterskuldAdjustment(companyId, asOf);
      setAdjustment(res.data?.data || res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte beräkna justering.');
    } finally {
      setLoadingAdjustment(false);
    }
  };

  const handleBook = async () => {
    const delta = Math.abs(Number(adjustment?.delta) || 0);
    if (delta < 0.005) {
      toast.info('Ingen justering behövs — 2940 matchar redan beräknad skuld.');
      return;
    }
    const ok = await formDialog({
      title: 'Bokföra semesterskuld?',
      description: (
        <>
          Per {asOf}: beräknad skuld på 2940 är {fmt(adjustment.targetLiability)} kr, nuvarande saldo {fmt(adjustment.currentBalance)} kr.
          Verifikatet justerar med {fmt(adjustment.delta)} kr ({adjustment.direction === 'increase' ? 'ökning' : 'minskning'}).
          En andra körning på samma datum ändrar ingenting.
        </>
      ),
      fields: [],
      confirmLabel: 'Bokför',
    });
    if (!ok) return;

    setBooking(true);
    setError(null);
    try {
      const res = await payrollApi.bookSemesterskuld(companyId, {
        asOf,
        description: description || undefined,
      });
      const data = res.data?.data || res.data;
      toast.success(`Verifikation ${data.verificationNumber || data.verificationId} skapad.`);
      await handleAdjustment();
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte bokföra semesterskuld.');
    } finally {
      setBooking(false);
    }
  };

  const noAdjustment = adjustment && Math.abs(Number(adjustment.delta) || 0) < 0.005;

  return (
    <Section
      id="skuld-heading"
      title="Semesterskuld"
      hint="Skulden per anställd är (tjänade + överförda + sparade − använda dagar) × daglön enligt procentregeln, med sociala avgifter. Bokförs på 2940 med motkonto 7290 (7091 för arbetare). Görs normalt vid bokslut. Samma datum bokförs aldrig två gånger."
      meta="Beräkna skulden, jämför med saldot på konto 2940 och bokför skillnaden."
    >
      {error && <Callout tone="danger" role="alert">{error}</Callout>}

      <div className="emp-form-page">
        <div className="form-grid" style={{ marginBottom: '1.25rem' }}>
          <div className="input-group">
            <label className="input-label" htmlFor="skuld-asof">Per datum</label>
            <div className="input-container">
              <input id="skuld-asof" type="date" className="modern-input" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
            </div>
          </div>
          <div className="input-group">
            <label className="input-label" htmlFor="skuld-text">Verifikationstext</label>
            <div className="input-container">
              <input id="skuld-text" type="text" className="modern-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={`Justering semesterskuld ${asOf}`} />
            </div>
          </div>
        </div>
      </div>

      <ol className="sub-steps">
        <li className={`sub-step ${liability ? 'is-done' : 'is-current'}`}>
          <span className="sub-step__num" aria-hidden="true">{liability ? <CheckCircle size={16} /> : '1'}</span>
          <div className="sub-step__body">
            <div className="sub-step__head">
              <h4 className="sub-step__title">Beräkna skulden</h4>
              <button type="button" className={`ll-btn ll-btn--sm ${liability ? 'll-btn--secondary' : 'll-btn--primary'}`} onClick={handleLiability} disabled={loadingLiability}>
                {loadingLiability ? <Spinner size="sm" animation="border" /> : <Calculator />}
                {liability ? 'Beräkna igen' : 'Beräkna'}
              </button>
            </div>
            <p className="sub-step__hint">Semesterskulden per anställd på det valda datumet.</p>
            {liability && (
              <div className="sub-step__content">
                <div className="sem__figures">
                  <div>
                    <span className="slip__label">Total skuld 2940 per {liability.asOf || asOf}</span>
                    <div style={{ fontSize: '1.125rem', fontWeight: 700 }}>{fmt(liability.totalLiability)} kr</div>
                  </div>
                </div>
                {Array.isArray(liability.perEmployee) && liability.perEmployee.length > 0 && (
                  <div className="ll-table-card table-responsive sem__result">
                    <table className="ll-table">
                      <thead>
                        <tr>
                          <th>Anställd</th>
                          <th className="ll-num">Dagar kvar</th>
                          <th className="ll-num">Daglön</th>
                          <th className="ll-num">Soc. avg.</th>
                          <th className="ll-num">Skuld</th>
                        </tr>
                      </thead>
                      <tbody>
                        {liability.perEmployee.map((r) => (
                          <tr key={r.employeeId}>
                            <td style={{ fontWeight: 500 }}>{r.employeeName || r.employeeId}</td>
                            <td className="ll-num">{fmt(r.liableDays)}</td>
                            <td className="ll-num">{fmt(r.dailyValue)}</td>
                            <td className="ll-num">{fmt(r.socialSecurity)}</td>
                            <td className="ll-num" style={{ fontWeight: 600 }}>{fmt(r.liability)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </li>

        <li className={`sub-step ${adjustment ? 'is-done' : liability ? 'is-current' : ''}`}>
          <span className="sub-step__num" aria-hidden="true">{adjustment ? <CheckCircle size={16} /> : '2'}</span>
          <div className="sub-step__body">
            <div className="sub-step__head">
              <h4 className="sub-step__title">Jämför med konto 2940</h4>
              <button type="button" className={`ll-btn ll-btn--sm ${adjustment ? 'll-btn--secondary' : 'll-btn--primary'}`} onClick={handleAdjustment} disabled={loadingAdjustment || !liability}>
                {loadingAdjustment ? <Spinner size="sm" animation="border" /> : <AlertTriangle />}
                {adjustment ? 'Jämför igen' : 'Jämför'}
              </button>
            </div>
            <p className="sub-step__hint">Skillnaden mellan den beräknade skulden och det som redan är bokfört.</p>
            {adjustment && (
              <div className="sub-step__content">
                <div className="sem__figures">
                  <div>
                    <span className="slip__label">Beräknad skuld</span>
                    <div>{fmt(adjustment.targetLiability)} kr</div>
                  </div>
                  <div>
                    <span className="slip__label">Bokfört på 2940</span>
                    <div>{fmt(adjustment.currentBalance)} kr</div>
                  </div>
                  <div>
                    <span className="slip__label">Justering</span>
                    <div style={{ fontWeight: 700 }}>
                      {fmt(adjustment.delta)} kr
                      {adjustment.direction && !noAdjustment && (
                        <StatusBadge tone={adjustment.direction === 'increase' ? 'warning' : 'muted'} className="ms-2">
                          {adjustment.direction === 'increase' ? 'Ökning' : 'Minskning'}
                        </StatusBadge>
                      )}
                    </div>
                  </div>
                </div>
                {noAdjustment && (
                  <Callout tone="success" className="mt-3 mb-0">Ingen justering behövs. Konto 2940 stämmer redan med den beräknade skulden.</Callout>
                )}
              </div>
            )}
          </div>
        </li>

        <li className={`sub-step ${adjustment && !noAdjustment ? 'is-current' : ''}`}>
          <span className="sub-step__num" aria-hidden="true">3</span>
          <div className="sub-step__body">
            <div className="sub-step__head">
              <h4 className="sub-step__title">Bokför justeringen</h4>
              <button type="button" className="ll-btn ll-btn--sm ll-btn--primary" onClick={handleBook} disabled={booking || !adjustment || noAdjustment}>
                {booking ? <Spinner size="sm" animation="border" /> : <BookOpen />}
                Bokför
              </button>
            </div>
            <p className="sub-step__hint">Skapar verifikatet som justerar 2940 mot 7290 med skillnaden ovan.</p>
          </div>
        </li>
      </ol>
    </Section>
  );
};

export default SemesterskuldSection;
